import { useTranslation } from 'react-i18next';
import { TripContent } from '../../services/api';
import RouteTimeline from './RouteTimeline';
import './TripHero.css';

interface TripHeroProps {
    content: TripContent;
    isLocked?: boolean;
    onToggleMap?: () => void;
    onExport?: () => void;
    onShare?: () => void;
    onAddToCalendar?: () => void;
    currentCityName?: string;
    currentCityDuration?: number;
    /** Route timeline in hero (desktop): show cities with max 3 visible + ellipsis */
    selectedCityIdx?: number;
    lastDayPerCity?: Record<number, number>;
    onSelectCity?: (cityIdx: number, dayIdx: number) => void;
    onEditRoutes?: () => void;
    isRouteEditable?: boolean;
}

/**
 * TripHero - Editorial block that scrolls with content (desktop).
 * Refined to match "Kyoto Exploration" style:
 * Left: Title + Location/Duration/Dates
 * Right: Actions (Map, Filters, Share, Calendar, Export)
 */
export default function TripHero({ 
    content, 
    isLocked = false,
    onToggleMap,
    onExport,
    onShare,
    onAddToCalendar,
    currentCityName,
    currentCityDuration,
    selectedCityIdx = 0,
    lastDayPerCity = {},
    onSelectCity,
    onEditRoutes,
    isRouteEditable = false,
}: TripHeroProps) {
    const { t } = useTranslation();

    const cityNames = content.cities.map(c => c.name);
    const mainCity = cityNames[0];
    const isMultiCity = cityNames.length > 1;
    const primaryCountry = content.cities[0]?.country || 'Destination';
    const totalDays = content.cities.reduce((sum, c) => sum + c.days.length, 0) || 1;
    
    // Title logic: Use current city + "Exploration" if multi-city and city is selected, else Trip Title
    const displayTitle = currentCityName 
        ? `${currentCityName} Exploration`
        : (content.title || `${mainCity} Trip`);

    // Location string
    const locationStr = currentCityName 
        ? `${currentCityName}, ${primaryCountry}` 
        : (isMultiCity ? 'Multi-City Trip' : `${mainCity}, ${primaryCountry}`);

    // Duration string
    const durationStr = currentCityDuration 
        ? `${currentCityDuration} ${t('common.days', 'Days')}`
        : `${totalDays} ${t('common.days', 'Days')}`;

    // Date range string
    const formatDate = (dateStr: string) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        } catch {
            return dateStr;
        }
    };
    const dateRangeStr = `${formatDate(content.dateRange.start)} – ${formatDate(content.dateRange.end)}`;

    return (
        <section className="trip-hero-slim" aria-label={displayTitle}>
            <div className="hero-content-wrapper">
                <div className="hero-left">
                    <h1 className="hero-title">{displayTitle}</h1>
                    <div className="hero-meta-row">
                        <div className="hero-location">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="pin-icon">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                            </svg>
                            <span>{locationStr}</span>
                        </div>
                        <span className="hero-dot">•</span>
                        <span className="hero-duration">{durationStr}</span>
                        <span className="hero-dot">•</span>
                        <span className="hero-dates">{dateRangeStr}</span>
                    </div>
                </div>

                <div className="hero-actions">
                    {onToggleMap && (
                        <button className="hero-action-btn" onClick={onToggleMap}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                                <line x1="8" y1="2" x2="8" y2="18" />
                                <line x1="16" y1="6" x2="16" y2="22" />
                            </svg>
                            {t('trip.show_map', 'Show Map')}
                        </button>
                    )}
                    <button className="hero-action-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="4" y1="21" x2="4" y2="14" />
                            <line x1="4" y1="10" x2="4" y2="3" />
                            <line x1="12" y1="21" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12" y2="3" />
                            <line x1="20" y1="21" x2="20" y2="16" />
                            <line x1="20" y1="12" x2="20" y2="3" />
                            <line x1="1" y1="14" x2="7" y2="14" />
                            <line x1="9" y1="8" x2="15" y2="8" />
                            <line x1="17" y1="16" x2="23" y2="16" />
                        </svg>
                        {t('common.filters', 'Filters')}
                    </button>
                    {onShare && (
                        <button className="hero-action-btn" onClick={onShare}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                            </svg>
                            {t('common.share', 'Share')}
                        </button>
                    )}
                    {onAddToCalendar && (
                        <button className="hero-action-btn" onClick={onAddToCalendar}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="12" y1="10" x2="12" y2="18" /><line x1="8" y1="14" x2="16" y2="14" />
                            </svg>
                            {t('calendar.add_to_calendar', 'Add to Calendar')}
                        </button>
                    )}
                    {onExport && (
                        <button className="hero-action-btn" onClick={onExport}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            {t('common.export', 'Export')}
                        </button>
                    )}
                </div>
            </div>

            {/* Route timeline: desktop only, centered, all cities */}
            {content.cities.length > 1 && onSelectCity && (
                <div className="hero-route-wrap">
                    <RouteTimeline
                        content={content}
                        selectedCityIdx={selectedCityIdx}
                        lastDayPerCity={lastDayPerCity}
                        onSelectCity={onSelectCity}
                        onEditRoutes={onEditRoutes ?? (() => {})}
                        isEditable={isRouteEditable}
                    />
                </div>
            )}

            {isLocked && (
                <p className="hero-trust-line">
                    {t('tripHero.trust_line', 'Preview is free — unlock only to edit & export')}
                </p>
            )}
        </section>
    );
}
